import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

export const defaultRole = 'button';
export const defaultAriaLabel = 'animation';
export const animationCompleteCssClass = 'animation-complete';

const Lottie = ({
    role,
    title,
    width,
    height,
    options,
    ariaLabel,
    className,
    customStyle
}) => {
    const ref = useRef({}).current;

    useEffect(() => {
        const container = ref.container;
        if (container && options?.animationData?.layers?.length) {
            let cancelled = false;
            import(/* webpackChunkName: "lottie-web" */ 'lottie-web')
                .then(({ default: lottie }) => {
                    if (cancelled) return;
                    const animationItem = lottie.loadAnimation({
                        container: container,
                        loop: true,
                        autoplay: true,
                        segments: true,
                        renderer: 'svg',
                        rendererSettings: {
                            preserveAspectRatio: 'xMidYMid slice'
                        },
                        ...options
                    });
                    if (animationItem && !options.loop) {
                        animationItem.onComplete = () => {
                            container.classList.add(animationCompleteCssClass);
                        };
                    }
                    ref.animationItem = animationItem;
                })
                .catch(() => {
                    if (APP_INFO.debug) {
                        console.debug('Failed to load lottie-web');
                    }
                });
            return () => {
                cancelled = true;
                ref.animationItem?.destroy();
            };
        }
    }, []);

    const getSize = initial => {
        return typeof 'number' === initial ? `${initial}px` : initial || '100%';
    };

    const style = {
        width: getSize(width),
        height: getSize(height),
        overflow: 'hidden',
        margin: '0 auto',
        outline: 'none',
        ...customStyle
    };

    return (
        <div
            tabIndex="0"
            role={role}
            aria-label={ariaLabel}
            className={`animation-wrapper ${className || 'standard'}`}
            title={title || ''}
            style={style}
            ref={el => {
                ref.container = el;
            }}
        />
    );
};

Lottie.propTypes = {
    role: PropTypes.string,
    title: PropTypes.string,
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    options: PropTypes.shape({
        loop: PropTypes.bool,
        autoplay: PropTypes.bool,
        segments: PropTypes.bool,
        animationData: PropTypes.object.isRequired,
        rendererSettings: PropTypes.object
    }).isRequired,
    ariaLabel: PropTypes.string,
    className: PropTypes.string,
    customStyle: PropTypes.object
};

Lottie.defaultProps = {
    role: defaultRole,
    ariaLabel: defaultAriaLabel
};

export default Lottie;
